import {
  Box,
  Flex,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text
} from '@chakra-ui/react';
import React from 'react';

import type {
  CaseProviderEntry,
  CaseProvidersData
} from '../../interface/caseProviders';
import type { SlotProps } from '../../interface/slotProps';
import {
  INK,
  INK_MUTED,
  INK_SOFT,
  POPOVER_BORDER,
  POPOVER_RADIUS,
  POPOVER_SHADOW,
  SURFACE_WARM
} from '../../styles/tokens';

const na = (v: unknown): string =>
  typeof v === 'string' || typeof v === 'number' ? String(v) : 'N/A';

const INTER_STACK =
  'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const HOVER_HINT_SX = {
  cursor: 'help',
  textDecoration: 'underline dotted var(--border-strong, #c9c8ce)',
  textUnderlineOffset: '3px',
  '&:hover, &:focus-visible': {
    textDecorationColor: 'var(--text-muted, #7a7682)'
  }
} as const;

const PersonIcon: React.FC<{ readonly size?: number }> = ({ size = 13 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4.4 3.6-8 8-8s8 3.6 8 8" />
  </svg>
);

const ATTENDING_BADGE = {
  bg: 'var(--ai-faint, #efeafc)',
  color: 'var(--ai, #6b4bd4)'
} as const;

const TYPE_BADGE = {
  bg: 'var(--panel-2, #eceef2)',
  color: 'var(--text-2, #5b5966)'
} as const;

const isNonEmptyString = (v: unknown): v is string =>
  typeof v === 'string' && v.trim() !== '';

type Badge = { readonly label: string; readonly bg: string; readonly color: string };

function cardBadge(provider: CaseProviderEntry): Badge | undefined {
  if (provider.isAttending) {
    return { label: 'Attending', ...ATTENDING_BADGE };
  }
  if (isNonEmptyString(provider.providerType)) {
    return { label: provider.providerType, ...TYPE_BADGE };
  }
  return undefined;
}

const ProviderCard: React.FC<{
  readonly provider: CaseProviderEntry;
  readonly index: number;
}> = ({ provider, index }) => {
  const roles = Array.isArray(provider.roles) ? provider.roles : [];
  const displayName = isNonEmptyString(provider.credentials)
    ? `${na(provider.name)}, ${provider.credentials}`
    : na(provider.name);
  const badge = cardBadge(provider);

  return (
    <Box
      position="relative"
      data-testid={`care-team-provider-${index}`}
      border={`1px solid ${POPOVER_BORDER}`}
      borderRadius="10px"
      bg="#ffffff"
      padding="10px 12px"
    >
      <Flex align="center" gap="8px" pr="34px" wrap="wrap">
        <Text as="span" fontWeight={600} fontSize="11.5px" color={INK}>
          {displayName}
        </Text>
        {badge && (
          <Box
            as="span"
            data-testid={`care-team-provider-${index}-badge`}
            fontSize="9px"
            fontWeight={700}
            textTransform="uppercase"
            letterSpacing="0.06em"
            borderRadius="4px"
            px="6px"
            py="2px"
            bg={badge.bg}
            color={badge.color}
          >
            {badge.label}
          </Box>
        )}
      </Flex>
      <Text
        fontSize="11px"
        color={INK_SOFT}
        fontStyle={isNonEmptyString(provider.specialty) ? 'normal' : 'italic'}
        mt="2px"
        pr="34px"
      >
        {isNonEmptyString(provider.specialty)
          ? provider.specialty
          : 'specialty missing'}
      </Text>
      {roles.length > 0 && (
        <Text
          data-testid={`care-team-provider-${index}-roles`}
          fontSize="9.5px"
          fontWeight={700}
          color="var(--ai, #6b4bd4)"
          mt="4px"
          pr="34px"
        >
          <Box as="span" fontSize="0.5em">
            {'◆'}
          </Box>{' '}
          {roles.map((role) => role.label).join(' · ')}
        </Text>
      )}
      <Box
        as="button"
        type="button"
        data-testid={`care-team-provider-${index}-details`}
        aria-label={`View details for ${na(provider.name)}`}
        title={`View details for ${na(provider.name)}`}
        onClick={() => {
          /* No-op for now (a later story wires the provider-details modal) */
        }}
        position="absolute"
        top="8px"
        right="8px"
        display="grid"
        placeItems="center"
        w="26px"
        h="26px"
        background="none"
        border="1px solid var(--border-strong, #c9c8ce)"
        borderRadius="7px"
        color="var(--text-muted, #7a7682)"
        cursor="pointer"
        _hover={{
          borderColor: 'var(--ai, #6b4bd4)',
          color: 'var(--ai, #6b4bd4)',
          background: ATTENDING_BADGE.bg
        }}
      >
        ✏
      </Box>
    </Box>
  );
};

export const CareTeam: React.FC<SlotProps> = ({ value }) => {
  const providers: readonly CaseProviderEntry[] =
    value != null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Array.isArray((value as CaseProvidersData).providers)
      ? (value as CaseProvidersData).providers
      : [];

  const billing = providers.find((p) =>
    p.roles.some((role) => role.id === 'billing')
  );
  const primary = billing ?? providers.find((p) => p.isAttending) ?? providers[0];
  const extra = Math.max(providers.length - (primary ? 1 : 0), 0);
  const extraProviderNoun = extra === 1 ? 'provider' : 'providers';
  const extraSuffix = extra > 0 ? `, plus ${extra} more ${extraProviderNoun}` : '';

  return (
    <Popover trigger="hover" placement="bottom-start">
      <PopoverTrigger>
        <Flex
          as="span"
          align="center"
          gap="5px"
          tabIndex={0}
          data-testid="care-team"
          aria-label={`Care team: ${na(primary?.name)}${extraSuffix}`}
        >
          <PersonIcon />
          <Text
            as="span"
            fontFamily={INTER_STACK}
            fontSize="12px"
            fontWeight={500}
            color={INK}
            sx={HOVER_HINT_SX}
          >
            {na(primary?.name)}
          </Text>
          {extra > 0 && (
            <Box
              as="span"
              data-testid="care-team-count"
              fontFamily={INTER_STACK}
              fontSize="10.5px"
              fontWeight={700}
              color={INK_MUTED}
              bg="var(--panel-2, #eceef2)"
              borderRadius="999px"
              px="6px"
              py="1px"
            >
              +{extra}
            </Box>
          )}
        </Flex>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          data-testid="care-team-popover"
          bg={SURFACE_WARM}
          border={`1px solid ${POPOVER_BORDER}`}
          borderRadius={POPOVER_RADIUS}
          boxShadow={POPOVER_SHADOW}
          padding="12px"
          width="max-content"
          minWidth="300px"
          maxWidth="380px"
          fontFamily={INTER_STACK}
        >
          <Text
            as="div"
            data-testid="care-team-eyebrow"
            fontSize="10px"
            fontWeight={700}
            letterSpacing="0.06em"
            textTransform="uppercase"
            color={INK_MUTED}
            mb="8px"
          >
            Care team &middot; {providers.length} provider
            {providers.length === 1 ? '' : 's'}
          </Text>
          <Flex direction="column" gap="8px">
            {providers.length === 0 ? (
              <Text fontSize="12px" color={INK_SOFT}>
                N/A
              </Text>
            ) : (
              providers.map((provider, index) => (
                <ProviderCard
                  key={`${provider.name}-${index}`}
                  provider={provider}
                  index={index}
                />
              ))
            )}
          </Flex>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
